import React, { useMemo, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Copy, Check, UploadCloud, X, ShieldCheck, ArrowRight, CircleDollarSign } from "lucide-react";
import api, { GHS } from "@/lib/api";
import { useSettings } from "@/context/SettingsContext";

const ACCEPT = ["image/jpeg", "image/jpg", "image/png", "image/webp"];

function compress(file) {
  return new Promise((resolve, reject) => {
    const img = new Image();
    const reader = new FileReader();
    reader.onload = (e) => { img.src = e.target.result; };
    reader.onerror = reject;
    img.onload = () => {
      const max = 1400;
      let { width, height } = img;
      if (width > max || height > max) { const r = Math.min(max / width, max / height); width = Math.round(width * r); height = Math.round(height * r); }
      const c = document.createElement("canvas"); c.width = width; c.height = height;
      c.getContext("2d").drawImage(img, 0, 0, width, height);
      resolve(c.toDataURL("image/jpeg", 0.82));
    };
    img.onerror = reject;
    reader.readAsDataURL(file);
  });
}

export default function Payment() {
  const { planName } = useParams();
  const navigate = useNavigate();
  const { settings } = useSettings();
  const inputRef = useRef(null);
  const [copied, setCopied] = useState(false);
  const [preview, setPreview] = useState(null);
  const [base64, setBase64] = useState(null);
  const [reference, setReference] = useState("");
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);

  const plan = useMemo(() => (settings?.plans || []).find((p) => p.name === decodeURIComponent(planName)), [settings, planName]);
  const number = settings?.payment_number || "";

  const copyNumber = () => {
    navigator.clipboard?.writeText(number).then(() => {
      setCopied(true); toast.success("Number copied!"); setTimeout(() => setCopied(false), 2000);
    }).catch(() => toast.error("Could not copy."));
  };

  const handleFile = async (file) => {
    if (!file) return;
    if (!ACCEPT.includes(file.type)) { toast.error("Use JPG, JPEG, PNG or WEBP."); return; }
    try { const d = await compress(file); setPreview(d); setBase64(d); } catch { toast.error("Could not read image."); }
  };

  const submit = async () => {
    if (!base64) { toast.error("Please upload your payment screenshot."); return; }
    setLoading(true);
    try {
      await api.post("/payments/submit", { plan_name: plan.name, proof_base64: base64, reference });
      setDone(true);
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Submission failed.");
    } finally { setLoading(false); }
  };

  if (!plan) return <div className="py-16 text-center text-slate-400">Plan not found. <button onClick={() => navigate("/plans")} className="text-emerald-400">View plans</button></div>;

  if (done) return (
    <div className="max-w-lg mx-auto text-center py-12" data-testid="payment-submitted">
      <div className="h-16 w-16 mx-auto grid place-items-center rounded-2xl bg-amber-500/15 text-amber-400 mb-5"><ShieldCheck size={32} /></div>
      <h1 className="font-display text-2xl font-black text-white">PENDING VERIFICATION</h1>
      <p className="mt-3 text-slate-400">Your payment is being reviewed. Your subscription will be activated after your payment is confirmed.</p>
      <div className="flex flex-col sm:flex-row gap-3 justify-center mt-7">
        <button onClick={() => navigate("/payment-history")} className="vw-btn-primary" data-testid="payment-view-history">View Payment Status</button>
        <button onClick={() => navigate("/dashboard")} className="vw-btn-outline" data-testid="payment-to-dashboard">Back to Dashboard</button>
      </div>
    </div>
  );

  return (
    <div className="max-w-3xl mx-auto" data-testid="payment-page">
      <p className="vw-label">Checkout</p>
      <h1 className="font-display text-2xl sm:text-3xl font-bold text-white mt-1 mb-6">Complete your payment</h1>

      {/* steps */}
      <div className="flex items-center gap-2 text-xs sm:text-sm text-slate-400 mb-6 flex-wrap">
        {["Select your plan", "Make payment", "Upload screenshot", "Submit for verification"].map((s, i) => (
          <React.Fragment key={i}>
            <span className={i === 0 ? "text-emerald-400 font-semibold" : ""}>{s}</span>
            {i < 3 && <ArrowRight size={13} className="text-slate-600" />}
          </React.Fragment>
        ))}
      </div>

      {/* selected plan */}
      <div className="vw-glow-card p-5 flex items-center justify-between mb-6" data-testid="payment-plan-summary">
        <div className="flex items-center gap-3">
          <CircleDollarSign className="text-emerald-400" size={24} />
          <div>
            <div className="font-display font-bold text-white">{plan.name}</div>
            <div className="text-sm text-slate-400">{plan.type === "time_pass" ? `Unlimited for ${plan.duration_hours}h` : `${plan.credits} analyses`}</div>
          </div>
        </div>
        <div className="font-display text-2xl font-black text-white">{GHS(plan.price)}</div>
      </div>

      {/* payment instructions */}
      <div className="vw-card p-6 mb-6">
        <h3 className="font-display font-bold text-white mb-1">MAKE PAYMENT TO</h3>
        <p className="text-sm text-slate-400 mb-4">{settings?.payment_instructions}</p>
        <div className="rounded-2xl p-6 text-center" style={{ background: "linear-gradient(135deg,#0E1A14,#111827)", border: "1px solid rgba(0,255,102,0.25)" }}>
          <div className="text-xs font-mono uppercase tracking-widest text-slate-400">Number</div>
          <div className="font-display text-3xl sm:text-4xl font-black text-white tracking-wide my-2" data-testid="payment-number">{number}</div>
          <div className="text-sm text-slate-300">Name: <span className="font-semibold">{settings?.payment_name}</span></div>
          <button onClick={copyNumber} className="vw-btn-primary mt-4 inline-flex items-center gap-2 !py-2.5" data-testid="copy-number-btn">
            {copied ? <><Check size={16} /> Number copied!</> : <><Copy size={16} /> COPY NUMBER</>}
          </button>
        </div>
      </div>

      {/* proof upload */}
      <div className="vw-card p-6 mb-6">
        <h3 className="font-display font-bold text-white mb-1">PAYMENT SCREENSHOT</h3>
        <p className="text-sm text-slate-400 mb-4">After making the payment, upload a screenshot/proof of the transaction.</p>
        {!preview ? (
          <div onClick={() => inputRef.current?.click()} className="cursor-pointer rounded-2xl border-2 border-dashed border-slate-700 hover:border-emerald-500/50 p-8 text-center transition-all" data-testid="proof-dropzone">
            <UploadCloud size={28} className="mx-auto text-emerald-400 mb-2" />
            <p className="text-sm text-slate-300 font-medium">Tap to upload proof</p>
            <p className="text-xs text-slate-500 font-mono mt-1">JPG · JPEG · PNG · WEBP</p>
            <input ref={inputRef} type="file" accept=".jpg,.jpeg,.png,.webp,image/*" className="hidden" onChange={(e) => handleFile(e.target.files?.[0])} data-testid="proof-input" />
          </div>
        ) : (
          <div className="relative rounded-xl overflow-hidden border border-slate-800" data-testid="proof-preview">
            <img src={preview} alt="proof" className="w-full max-h-72 object-contain bg-black/40" />
            <button onClick={() => { setPreview(null); setBase64(null); }} className="absolute top-3 right-3 h-9 w-9 grid place-items-center rounded-full bg-black/70 text-white" data-testid="proof-remove"><X size={18} /></button>
          </div>
        )}
        <div className="mt-4">
          <label className="block text-sm font-medium text-slate-300 mb-1.5">Payment reference (optional)</label>
          <input value={reference} onChange={(e) => setReference(e.target.value)} placeholder="Transaction ID / reference" className="vw-input" data-testid="payment-reference" />
        </div>
      </div>

      <button onClick={submit} disabled={loading} className="vw-btn-primary w-full !py-3.5 inline-flex items-center justify-center gap-2 disabled:opacity-60" data-testid="submit-payment-btn">
        {loading ? "Submitting…" : <><ShieldCheck size={18} /> SUBMIT PAYMENT FOR VERIFICATION</>}
      </button>
    </div>
  );
}
